import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { executionsApi, ordersApi, stagesApi } from "@/lib/api";
import { displayName, formatDateTime, formatRelative, shortId } from "@/lib/format";
import type { OperationType } from "@/lib/types";

const OP_TONE: Record<OperationType, string> = {
  PRODUCTION: "pill-accent",
  QC: "pill-warn",
  ACCUMULATION: "pill-info",
  TECHNOLOGICAL: "",
  PRINTING: "pill-info",
};

export default function StageDetail() {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const stageId = Number(params.id);

  const detailed = useQuery({
    queryKey: ["stage", stageId, "detailed"],
    queryFn: () => stagesApi.detailed(stageId),
    enabled: !Number.isNaN(stageId),
  });

  const orders = useQuery({
    queryKey: ["orders", "by-stage", stageId],
    queryFn: () => ordersApi.list({ stage_id: stageId, page_size: 50 }),
    enabled: !Number.isNaN(stageId),
  });

  const executions = useQuery({
    queryKey: ["executions", "by-stage", stageId],
    queryFn: () => executionsApi.list({ page_size: 50 }),
    enabled: !Number.isNaN(stageId),
    refetchInterval: 5000,
  });

  if (Number.isNaN(stageId)) return <ErrorState error={new Error("Bad stage id")} />;
  if (detailed.isLoading) return <LoadingState />;
  if (detailed.isError) return <ErrorState error={detailed.error} />;

  const stage = detailed.data!;
  const stageOps = new Set(stage.operations.map((op) => op.id));
  const stageExecs =
    executions.data?.items.filter((e) => stageOps.has(e.operation_id)) ?? [];
  const openExecs = stageExecs.filter(
    (e) => e.status === "IN_PROGRESS" || e.status === "STARTED" || e.status === "QC_PENDING",
  );

  return (
    <>
      <PageHeader
        eyebrow={`stage · ${stage.code}`}
        title={displayName(stage)}
        actions={<Link to="/stages" className="btn">{t("stageDetail.allStages")}</Link>}
      />

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wider font-mono text-text-muted">{t("stageDetail.kpiOperations")}</p>
          <p className="text-4xl font-serif text-text-h1 mt-2">{stage.operations.length}</p>
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wider font-mono text-text-muted">{t("stageDetail.kpiOrders")}</p>
          <p className="text-4xl font-serif text-text-h1 mt-2">{orders.data?.pagination.total ?? "—"}</p>
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wider font-mono text-text-muted">{t("stageDetail.kpiOpenExecutions")}</p>
          <p className="text-4xl font-serif text-text-h1 mt-2">{openExecs.length}</p>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading">{t("stageDetail.operations")}</h2>
        <div className="mt-4 -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-2 py-2">{t("stageDetail.colCode")}</th>
                <th className="px-2 py-2">{t("stageDetail.colType")}</th>
                <th className="px-2 py-2">{t("stageDetail.colEquipment")}</th>
                <th className="px-2 py-2">{t("stageDetail.colTransformation")}</th>
              </tr>
            </thead>
            <tbody>
              {stage.operations.map((op) => (
                <tr key={op.id} className="border-t border-border">
                  <td className="px-2 py-2 font-mono">{op.code}</td>
                  <td className="px-2 py-2">
                    <span className={`pill ${OP_TONE[op.type] ?? ""}`}>{op.type}</span>
                  </td>
                  <td className="px-2 py-2 font-mono text-text-muted">{op.equipment_code ?? "—"}</td>
                  <td className="px-2 py-2 text-text-secondary font-mono text-xs">
                    {op.transformation_rule ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading flex items-center justify-between">
          {t("stageDetail.productionOrders")}
          <Link
            to={`/orders?stage=${stage.id}`}
            className="text-xs font-normal text-accent-strong hover:underline"
          >
            {t("common.all")}
          </Link>
        </h2>
        <div className="mt-4 -mx-2">
          {orders.isLoading ? (
            <LoadingState />
          ) : (orders.data?.items.length ?? 0) === 0 ? (
            <EmptyState title={t("stageDetail.noOrders")} />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-text-muted font-mono text-xs uppercase">
                  <th className="px-2 py-2">{t("stageDetail.colCode")}</th>
                  <th className="px-2 py-2">{t("stageDetail.colPlan")}</th>
                  <th className="px-2 py-2">{t("stageDetail.colDone")}</th>
                  <th className="px-2 py-2">{t("stageDetail.colRejected")}</th>
                  <th className="px-2 py-2">{t("stageDetail.colStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {orders.data!.items.map((o) => (
                  <tr key={o.id} className="border-t border-border hover:bg-surface-hover">
                    <td className="px-2 py-2 font-mono">
                      <Link to={`/orders/${o.id}`} className="text-accent-strong hover:underline">{o.code}</Link>
                    </td>
                    <td className="px-2 py-2 tabular-nums">{o.target_quantity}</td>
                    <td className="px-2 py-2 tabular-nums">{o.completed_quantity}</td>
                    <td className={`px-2 py-2 tabular-nums ${o.rejected_quantity > 0 ? "text-danger" : "text-text-muted"}`}>
                      {o.rejected_quantity}
                    </td>
                    <td className="px-2 py-2"><StatusPill kind="order" value={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading">{t("stageDetail.openExecutions")}</h2>
        <div className="mt-4">
          {openExecs.length === 0 ? (
            <p className="text-text-muted text-sm">{t("stageDetail.noOpenExecutions")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-text-muted font-mono text-xs uppercase">
                  <th className="px-2 py-2">Exec</th>
                  <th className="px-2 py-2">{t("stageDetail.colOperation")}</th>
                  <th className="px-2 py-2">{t("stageDetail.colStation")}</th>
                  <th className="px-2 py-2">{t("stageDetail.colStatus")}</th>
                  <th className="px-2 py-2">Started</th>
                </tr>
              </thead>
              <tbody>
                {openExecs.map((e) => {
                  const op = stage.operations.find((o) => o.id === e.operation_id);
                  return (
                    <tr key={e.id} className="border-t border-border">
                      <td className="px-2 py-2 font-mono text-text-muted">{shortId(e.id)}</td>
                      <td className="px-2 py-2 font-mono">{op?.code ?? shortId(e.operation_id)}</td>
                      <td className="px-2 py-2 font-mono text-text-muted">{e.station_id ?? "—"}</td>
                      <td className="px-2 py-2"><StatusPill kind="execution" value={e.status} /></td>
                      <td className="px-2 py-2 text-text-muted text-xs">{formatRelative(e.started_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading">{t("stageDetail.opConfig")}</h2>
        <p className="text-text-muted text-xs mt-1 font-mono">
          {t("stageDetail.opConfigHint")}
        </p>
        {stage.operations.length === 0 ? (
          <p className="mt-4 text-text-muted text-sm">{t("stageDetail.noOperations")}</p>
        ) : (
          <pre className="mt-4 p-4 bg-surface-alt rounded-md overflow-x-auto text-xs font-mono">
            {JSON.stringify(
              stage.operations.map((op) => ({
                code: op.code,
                type: op.type,
                equipment_code: op.equipment_code,
                document_code: op.document_code,
                transformation_rule: op.transformation_rule,
                config: op.config,
              })),
              null,
              2,
            )}
          </pre>
        )}
        <p className="text-text-muted text-xs mt-3 font-mono">
          stage created: {formatDateTime(stage.created_at)} · updated: {formatDateTime(stage.updated_at)}
        </p>
      </section>
    </>
  );
}
